import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import kitopiAppWithHand from '@/assets/images/kitopiAppWithHand.png';
import { KITOPI_APP_STORE_ADD_TO_CART } from '@/constants/appStores';
import { BUTTON_SOURCES } from '@/constants/buttonSources';
import { useBrowserUserId } from '@/hooks/useBrowserUserId';
import { useGetCashbackPercentageValue } from '@/hooks/useGetCashbackPercentageValue.ts';
import { useLanguage } from '@/hooks/useLanguage';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import { trackButtonClick } from '@/utils/gtmButtonClicked';
import { buildUrlWithParams } from '@/utils/queryParams';
import { BottomSheet } from '../BottomSheet/BottomSheet';
import styles from './OrderPromptModal.module.scss';

interface OrderPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const OrderPromptModal = ({ isOpen, onClose }: OrderPromptModalProps) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const browserUserId = useBrowserUserId();
  const { queryParams } = useValidateQueryParams();
  const { brandId, tableNumber, counterNumber, outletId } = queryParams();
  const cashbackPercentageValue = useGetCashbackPercentageValue();

  const handleDownloadApp = useCallback(() => {
    const extraParams: Record<string, string> = {
      browser_user_id: browserUserId,
    };

    if (outletId) {
      extraParams.outletId = outletId;
    }

    trackButtonClick({
      buttonId: 'order-prompt-download-app',
      buttonName: t('menu.orderPromptModal.downloadApp'),
      buttonSource: BUTTON_SOURCES.ORDER_PROMPT_MODAL,
      urlParams: {
        brandId: brandId || undefined,
        tableNumber,
        counterNumber,
      },
      extraParams,
    });

    const deepLinkUrl = buildUrlWithParams(KITOPI_APP_STORE_ADD_TO_CART, {
      outletId: outletId || undefined,
      tableNumber: tableNumber || undefined,
      brandId: brandId || undefined,
    });

    window.open(deepLinkUrl, '_blank');
  }, [browserUserId, brandId, tableNumber, counterNumber, outletId, t]);

  if (!isOpen) return null;

  return (
    <BottomSheet closable={true} onClose={onClose}>
      <div className={`${styles.container} ${isRTL ? styles.rtl : ''}`}>
        <div className={styles.iconWrapper}>
          <img src={kitopiAppWithHand} alt="KitopiApp" className={styles.phoneIcon} />
        </div>

        <h3 className={styles.title}>{t('menu.orderPromptModal.title')}</h3>

        <p className={styles.description}>
          {t('menu.orderPromptModal.description', { cashbackPercentageValue })}
        </p>

        <button className={styles.downloadButton} onClick={handleDownloadApp}>
          {t('menu.orderPromptModal.downloadApp')}
        </button>
      </div>
    </BottomSheet>
  );
};
